// eslint-disable-next-line @nx/workspace/no-restricted-imports-clone
import {
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
} from '@chakra-ui/react';
import { Box, Flex, Text } from '@joinrepublic/dls-ui-kit';

import { HEADER_THEMES } from '../../types';
import { getRegionFlag } from '../../utils/get-region-flag';
import { getTextColor } from '../../utils/get-text-color';
import { GlobeIcon } from './icons';
import { RegionSwitcherDialog } from './region-switcher-dialog';
import { RegionSwitcherItem } from './region-switcher-item';
import { IRegionSwitcherSettings } from './types'; // Import the missing interface
import { useRegionSwitcher } from './use-region-switcher'; // Import the missing hook

export interface IRegionSwitcherDesktopProps {
  variant: HEADER_THEMES;
  regionalSwitcherSettings: IRegionSwitcherSettings;
}

export function RegionSwitcherDesktop({
  variant: currentVariant,
  regionalSwitcherSettings,
}: IRegionSwitcherDesktopProps) {
  const {
    variants,
    activeVariantID,
    onSetVariant,
    onModalClose,
    onConfirmVariant,
    isModalOpen,
    proposedVariant,
  } = useRegionSwitcher(regionalSwitcherSettings);

  const { titleName: name } =
    variants.find((v) => v.id === activeVariantID) ?? variants[0];

  /*
    A real button: Chakra puts `aria-haspopup`/`aria-expanded`/`aria-controls` on this child but
    no `tabIndex`, so as a div it carried ARIA it may not own and the region switcher was
    mouse-only — in the global header, on every page (DFI-2279). The visible region name stays
    inside the accessible name, satisfying "Label in Name".
  */
  const popoverTrigger = (
    <Flex
      as="button"
      type="button"
      aria-label={`Region: ${name}`}
      _hover={
        currentVariant !== 'dark' && currentVariant !== 'new-homepage'
          ? { bg: 'grey.50', color: 'grey.900' }
          : {}
      }
      borderRadius="lg"
      p="1"
      color={getTextColor(currentVariant)}
      cursor="default"
      alignItems="center"
    >
      {activeVariantID === 'global' ? (
        <GlobeIcon boxSize="6" />
      ) : (
        getRegionFlag(activeVariantID)
      )}
      <Text
        as="span"
        verticalAlign="middle"
        ml="1"
        color={getTextColor(currentVariant)}
      >
        {name}
      </Text>
    </Flex>
  );

  return (
    <>
      <Popover trigger="hover" placement="bottom-end">
        <PopoverTrigger>{popoverTrigger}</PopoverTrigger>
        <PopoverContent
          borderColor="transparent"
          boxShadow="base"
          w="152px"
          flexDir="row"
        >
          <PopoverArrow />
          <PopoverBody p="0" px="0" w="full">
            <Box>
              {variants.map((variant) => (
                <RegionSwitcherItem
                  key={variant.id}
                  p="3"
                  title={variant.titleName}
                  icon={getRegionFlag(variant.id)}
                  isActive={variant.id === activeVariantID}
                  onClick={() => onSetVariant(variant)}
                />
              ))}
            </Box>
          </PopoverBody>
        </PopoverContent>
      </Popover>
      <RegionSwitcherDialog
        isOpen={isModalOpen}
        onClose={onModalClose}
        onConfirm={onConfirmVariant}
        variant="modal"
        targetRegion={proposedVariant}
        regionalSwitcherSettings={regionalSwitcherSettings}
      />
    </>
  );
}
